/**
 * Criatura do mundo. Máquina de estados clara (IDLE, CHASE, ATTACK, HIT, DEAD) + comportamentos
 * da Fase 8: melee, lunge (Shardfang), charge (Craghorn), ranged (Aetherwing), guard (Glintback),
 * warden (Shard Warden). Voo + queda pelo Hook (grounded). Modelos procedurais estilizados
 * (CreatureBuilder) com animação procedural (gait, breathe, lean, hit, death).
 * Implementa Damageable (o player o atinge) e usa o mesmo DamageSystem contra o player.
 */
import * as THREE from "three";
import { GameConfig, type EnemyDef, type EnemyShape } from "../config/GameConfig";
import type { Arena } from "../world/Arena";
import type { CombatStats } from "../combat/CombatStats";
import type { DamageType } from "../items/EquipmentTypes";
import type { Damageable } from "../combat/Damageable";
import type { HookReaction, HookTarget } from "../player/HookSystem";
import type { TelegraphSystem } from "../fx/Telegraph";
import type { ParticleSystem } from "../fx/Particles";
import { HealthComponent } from "../combat/HealthComponent";
import { dealDamage } from "../combat/DamageSystem";
import { events } from "../core/Events";
import { dampAngle } from "../utils/math";
import { wallBetween } from "../world/TerritoryGates";
import { buildCreature, addChampionCrest, type CreatureRig } from "./CreatureBuilder";
import { createEnemyVisual, type RiggedVisual } from "../models";
import { Threat } from "../progression/Threat";
import { applyDissolve, type DissolveUniform } from "../fx/Dissolve";
import { applyTierLook } from "./CreatureBuilder";
import type { WeaponType } from "../items/EquipmentTypes";

export type EnemyState = "idle" | "chase" | "attack" | "hit" | "dead";

/** Dispara um projétil de inimigo -> player (injetado pelo Game). */
export type FireBoltFn = (origin: THREE.Vector3, dirX: number, dirZ: number, speed: number, damage: number, color: number) => void;

const _toPlayer = new THREE.Vector3();
const _toHome = new THREE.Vector3();
const _stormFrom = new THREE.Vector3();

// 10.5K — tipo de dano do golpe pela SILHUETA (def.damageType sobrescreve). Assim a defesa
// por tipo do player (Void Tree) tem leitura: garras/presas cortam, ferrões/bicos perfuram,
// massas/carapaças esmagam.
const SHAPE_DAMAGE: Record<EnemyShape, DamageType> = {
  orb: "piercing", runner: "slashing", brute: "blunt", beast: "slashing", heavy: "blunt",
  flyer: "piercing", shell: "blunt", warden: "slashing", strider: "slashing", spark: "piercing",
  bulwark: "blunt", stalker: "slashing", sentinel: "blunt",
};
export function enemyDamageType(def: EnemyDef): DamageType {
  return def.damageType ?? SHAPE_DAMAGE[def.shape] ?? "blunt";
}

export class Enemy implements Damageable, HookTarget {
  readonly group = new THREE.Group();
  readonly health: HealthComponent;
  readonly combatStats: CombatStats;
  readonly radius: number;

  state: EnemyState = "idle";
  removeRequested = false;
  /** 10.5M — território (gangue) a que este inimigo pertence; null = spawn avulso. */
  territoryId: string | null = null;
  /** 10.5M — é o CAMPEÃO (elite líder) do território? (10.9BT: ao virar campeão ganha a coroa no rig) */
  private _champion = false;
  get champion(): boolean {
    return this._champion;
  }
  set champion(v: boolean) {
    if (v && !this._champion) addChampionCrest(this.rig, this.def.elite?.aura ?? this.def.crystalColor ?? 0xffd24a);
    this._champion = v;
  }
  /** 10.9AC — é um CORROMPIDO do evento Invasão do Vazio? (dá stacks de buff temporário) */
  eventMonster = false;
  /** 10.9HX — monstro AMBIENTE da Fenda dos Titãs (ronda os corredores; não é add de boss).
   *  O Game limpa os adds que sobram quando não há boss, mas preserva estes. */
  zoneAmbient = false;
  pulled = false; // quando o Hook está puxando, a IA de movimento pausa

  private readonly velocity = new THREE.Vector3();
  private readonly knockback = new THREE.Vector3();
  private readonly home = new THREE.Vector3();
  private facing = 0;
  private attackCooldown = 0;
  private attackWindup = 0;
  private hitStun = 0;
  private flash = 0;
  private deathTimer = 0;
  private bob = Math.random() * Math.PI * 2;

  // ---- Combate / regeneração / retorno ----
  private readonly enemyCfg = GameConfig.enemyCombat;
  private combatTimer = 999;
  private returning = false;
  // 10.9CG — perambular em idle
  private readonly wanderTarget = new THREE.Vector3();
  private wandering = false;
  private wanderWait = Math.random() * 3;
  private dtNow = 1 / 60;

  // ---- Fase 8: comportamento estendido ----
  private readonly behavior: NonNullable<EnemyDef["behavior"]>;
  private readonly isFlyer: boolean;
  private grounded = false; // flyer derrubado pelo Hook (vulnerável no chão)
  private groundedTimer = 0;
  private groundImpactDone = false;
  private dashState: "none" | "telegraph" | "dash" | "recover" = "none";
  private dashTimer = 0;
  private dashCd = 0;
  private readonly dashDir = new THREE.Vector3();
  private dashTravel = 0;
  private dashHitDone = false;
  private dashTeleId = 0;
  private rangedCd = 0;
  private rangedWindup = 0; // >0 = carregando tiro
  private guardActive = false;
  private guardTimer = 0;
  private guardCd = 0;
  private wardenAttack = 0; // rotação de ataques do warden
  private fireBolt: FireBoltFn | null = null;
  // ---- Fase 10: novos comportamentos ----
  private burstLeft = 0; // Arcbeak: tiros restantes do burst
  private strafeSign: number = Math.random() < 0.5 ? 1 : -1; // Galestrider: sentido do círculo
  private strafeFlip = 2.5; // s até inverter o sentido do strafe
  private fadeOpacity = 1; // Cloudstalker: opacidade atual (fade parcial)
  private tempestAttack = 0; // Tempest Warden: rotação de ataques
  private stormCd = 0;
  private readonly stormZones: { x: number; z: number; t: number }[] = []; // Storm Field pendente
  private surgeDmg = 1; // Rift Surge: multiplicador de dano (ataques especiais)
  private readonly dmgScale: number = 1; // 10.5B: Ameaça Crescente (fixado no spawn)
  private readonly dmgType: DamageType; // 10.5K: tipo do golpe normal (defesa por tipo do player)
  private enrageTimer = 0; // 10.6O: fúria pós-hit — monstro corre mais rápido por 1.5s

  // ---- Elite (Ground Smash) ----
  private readonly isElite: boolean;
  private smashCd = 0;
  private smashWindup = 0;
  private smashTeleId = 0;
  private readonly smashCenter = new THREE.Vector3();
  private fxTelegraph: TelegraphSystem | null = null;
  private fxParticles: ParticleSystem | null = null;
  private auraMat: THREE.MeshBasicMaterial | null = null;
  private auraPulse = 0;

  // ---- Modelo procedural + animação ----
  private readonly rig: CreatureRig;
  /** 10.9GS — uniforms de dissolução (um por material de corpo); 0 = inteiro, 1 = sumiu. */
  private readonly dissolve: DissolveUniform[] = [];
  // GLB rigado (null enquanto não há GLB p/ este def.id → usa o rig procedural, §12).
  private readonly rigged: RiggedVisual | null;
  private gaitPhase = Math.random() * Math.PI * 2;
  private readonly baseHeight: number;
  private lean = 0;

  constructor(
    private readonly def: EnemyDef,
    private readonly arena: Arena,
    spawn: THREE.Vector3,
  ) {
    this.radius = def.radius;
    this.behavior = def.behavior ?? "melee";
    this.isElite = !!def.elite;
    this.isFlyer = !!def.flying;
    // 10.5B — Ameaça Crescente: escala vida/dano pelo poder do player (fixado no spawn)
    this.dmgScale = Threat.dmgMult;
    this.dmgType = enemyDamageType(def);
    this.combatStats = { baseDamage: def.damage * this.dmgScale, critChance: 0, critDamageMult: 1, armor: def.armor ?? 0 };
    this.smashCd = def.elite ? def.elite.groundSmash.cooldown * 0.5 : 0;
    this.health = new HealthComponent(Math.round(def.maxHp * Threat.hpMult), undefined, () => this.die());

    // modelo procedural estilizado (sempre construído: é o fallback)
    this.rig = buildCreature(def);
    this.group.add(this.rig.group);
    this.baseHeight = this.rig.height;
    for (const m of this.rig.bodyMats) this.dissolve.push(applyDissolve(m, def.crystalColor ?? 0xc9a6ff)); // 10.9GS

    // GLB rigado, se disponível: esconde o procedural e conduz a animação pelo mixer.
    this.rigged = createEnemyVisual(def.id, { color: def.color, emissive: def.crystalColor ?? def.color, emissiveIntensity: 0.35 });
    if (this.rigged) {
      this.rig.group.visible = false;
      this.group.add(this.rigged.root);
      this.rigged.anim.play("Idle", { crossfade: 0 });
    }

    // aura de Elite
    if (def.elite) {
      this.auraMat = new THREE.MeshBasicMaterial({ color: def.elite.aura, transparent: true, opacity: 0.5, depthWrite: false, side: THREE.DoubleSide });
      const aura = new THREE.Mesh(new THREE.RingGeometry(def.radius * 1.1, def.radius * 1.5, 40), this.auraMat);
      aura.rotation.x = -Math.PI / 2;
      aura.position.y = 0.05;
      this.group.add(aura);
      for (const m of this.rig.bodyMats) m.emissiveIntensity = Math.max(m.emissiveIntensity, 0.45);
    }

    this.group.scale.setScalar(def.scale);
    this.group.position.copy(spawn);
    this.group.position.y = 0;
    this.home.copy(this.group.position);
  }

  // ---- Damageable ----
  get position(): THREE.Vector3 {
    return this.group.position;
  }
  get isDead(): boolean {
    return this.state === "dead";
  }
  get armor(): number {
    return this.combatStats.armor;
  }
  /** Redução de dano (Glintback Crystal Guard). O DamageSystem lê isto (cap 0.8). */
  get damageReduction(): number {
    return this.guardActive ? this.def.guard?.reduction ?? 0 : 0;
  }
  get elite(): boolean {
    return this.isElite;
  }
  /** 10.7N — rank p/ o dano percentual que fura a defesa do player (campeão > elite). */
  get attackerRank(): "elite" | "champion" | undefined {
    return this.champion ? "champion" : this.isElite ? "elite" : undefined;
  }
  /** 10.6G — arma que este monstro TEME / arma que ele AGUENTA (lidas pelo DamageSystem). */
  get weakTo(): WeaponType | undefined {
    return this.def.weakTo;
  }
  get resists(): WeaponType | undefined {
    return this.def.resists;
  }
  get tough(): WeaponType | undefined {
    return this.def.tough;
  }
  /** Está voando (fora do alcance de melee)? Falso quando derrubado (grounded). */
  get flying(): boolean {
    return this.isFlyer && !this.grounded;
  }
  /** Fator de velocidade do pull do Hook (peso: Craghorn/Warden puxam devagar). */
  get hookPullFactor(): number {
    return 1 - (this.def.hookPullResist ?? 0);
  }
  get inCombat(): boolean {
    return this.combatTimer < this.enemyCfg.disengage;
  }
  get healthFraction(): number {
    return this.health.fraction;
  }
  get hp(): number {
    return this.health.current;
  }
  get maxHp(): number {
    return this.health.max;
  }
  get nameKey(): string {
    return `enemy.${this.def.id}`;
  }
  get barAnchorY(): number {
    return this.baseHeight * this.def.scale + 0.5;
  }

  attachFx(telegraph: TelegraphSystem, particles: ParticleSystem): void {
    this.fxTelegraph = telegraph;
    this.fxParticles = particles;
  }
  /** Injeta a função de disparo (behaviors ranged/warden). */
  setBoltFn(fn: FireBoltFn): void {
    this.fireBolt = fn;
  }

  /** Reação à fisgada do Hook. Flyer => derruba (grounded). Elite => stagger. Comum => puxa. */
  onHooked(): HookReaction {
    this.rigged?.anim.playOnce(this.isFlyer ? "Grounded" : "Hooked", "Idle"); // §8 (mecânica não muda)
    if (this.isFlyer) {
      this.grounded = true;
      this.groundedTimer = this.def.groundedDuration ?? GameConfig.hook.groundDuration;
      this.groundImpactDone = false;
      this.hitStun = Math.max(this.hitStun, 0.2);
      this.flash = 0.16;
      this.state = "hit";
      // reação da asa/energia ao ser fisgado (antes da queda)
      for (const m of this.rig.crystalMats) m.emissiveIntensity = 2.6;
      this.fxParticles?.burst(this.position.x, 1.6, this.position.z, this.def.crystalColor ?? 0x8fe8ff, { count: 8, speed: 4, dur: 0.4 });
      return "pull"; // puxa para perto e cai
    }
    // Stormback: Hook quebra o Static Shield + stagger curto (janela de punição)
    if (this.behavior === "shield" && this.guardActive && this.def.guard?.hookBreaks) {
      this.setGuard(false);
      this.guardCd = this.def.guard.cooldown; // escudo entra em cooldown
      this.hitStun = Math.max(this.hitStun, this.def.guard.stagger ?? 1.2);
      this.flash = 0.16;
      this.state = "hit";
      this.fxParticles?.burst(this.position.x, 1.2, this.position.z, this.def.crystalColor ?? 0x8fd8ff, { count: 16, speed: 6, dur: 0.5 });
      return "grab";
    }
    if (!this.def.elite) return "pull";
    if (this.smashWindup > 0) {
      this.fxTelegraph?.cancel(this.smashTeleId);
      this.smashTeleId = 0;
      this.smashWindup = 0;
    }
    this.hitStun = Math.max(this.hitStun, this.def.elite.hookStagger);
    this.flash = 0.16;
    this.state = "hit";
    return "grab";
  }

  onHit(): void {
    if (this.isDead) return;
    this.hitStun = 0.06;
    this.flash = 0.12;
    this.combatTimer = 0;
    this.enrageTimer = 1.5;
    this.state = "hit";
    this.rigged?.anim.playOnce("Hit", "Idle");
  }

  applyKnockback(dir: THREE.Vector3, force: number): void {
    this.knockback.addScaledVector(dir, force * (1 - this.def.knockbackResist) * 0.5);
  }

  /** Rift Surge (Fase 10): escala HP e dano no spawn (modificadores LEVES, §36). */
  applySurge(hpMult: number, dmgMult: number): void {
    // 10.9BM — BUG: recalculava a partir do BASE e jogava fora a Ameaça Crescente (monstro de Surge
    // nascia com vida/dano de novato). Agora multiplica o que já está aplicado.
    if (hpMult !== 1) {
      this.health.max = Math.round(this.health.max * hpMult);
      this.health.current = this.health.max;
    }
    this.combatStats.baseDamage *= dmgMult; // ataque básico
    this.surgeDmg *= dmgMult; // ataques especiais (dash/smash/storm/ranged)
    this.capHitsToKill();
  }

  /**
   * 10.6F — PESO DO TERRITÓRIO. Os territórios já tinham tier 1/2/3 no mapa, mas o tier não
   * mexia em NADA: um bicho do território mais perigoso da região nascia com a mesma vida e
   * o mesmo dano do território inicial. Era por isso que as áreas avançadas não matavam
   * ninguém. Multiplica por cima do que já foi aplicado (Ameaça Crescente e Surge).
   */
  /** 10.9DY — tier do territorio onde nasceu (pesa no TETO DE GOLPES). */
  tier = 1;
  /** 10.9HK — veste a criatura com a cara do tier do território (chamado pelo spawner). */
  applyTierLook(): void { if (!this.rigged) applyTierLook(this.rig, this.tier, this.def.crystalColor ?? this.def.color); }

  applyTierScale(hpMult: number, dmgMult: number): void {
    if (hpMult !== 1) {
      this.health.max = Math.round(this.health.max * hpMult);
      this.health.current = this.health.max;
    }
    this.combatStats.baseDamage *= dmgMult;
    this.surgeDmg *= dmgMult;
    this.capHitsToKill();
  }

  /**
   * 10.9DT — TETO DE GOLPES. A Ameaca Crescente ja escala a vida pelo dano do player, mas
   * tier de territorio + profundidade + surge multiplicavam por cima, e o bicho comum passava
   * de 1 MILHAO de vida (250+ golpes com 3k de dano). Limita a vida FINAL a N golpes do dano
   * efetivo do player, com piso na vida BASE do bicho (nao enfraquece nada no inicio do jogo).
   */
  capHitsToKill(): void {
    const c = GameConfig.threat.hitsCap;
    const base = c.byTier[Math.max(1, Math.min(c.byTier.length, this.tier)) - 1];
    const hits = base * (this.champion ? c.championMult : this.isElite ? c.eliteMult : 1);
    const cap = Math.max(this.def.maxHp, Math.round(Threat.playerDamage * hits));
    if (this.health.max <= cap) return;
    this.health.max = cap;
    this.health.current = cap;
  }

  // ---- loop ----
  /** 10.9FC — LENTIDAO (Nevasca do mago): multiplica o deslocamento enquanto dura. */
  slowTimer = 0;
  slowMult = 1;
  applySlow(mult: number, seconds: number): void {
    this.slowMult = Math.min(this.slowTimer > 0 ? this.slowMult : 1, mult);
    this.slowTimer = Math.max(this.slowTimer, seconds);
  }
  update(dt: number, player: Damageable, playerSafe = false): void {
    if (this.slowTimer > 0) { this.slowTimer -= dt; if (this.slowTimer <= 0) this.slowMult = 1; }
    this.rigged?.update(dt); // avança o AnimationMixer (no-op no fallback procedural)
    if (this.isDead) {
      this.updateDeath(dt);
      return;
    }
    const engageable = !player.isDead && !playerSafe;
    this.dtNow = dt; // 10.9CG — o perambular (dentro de decideAndMove) precisa do dt

    this.attackCooldown = Math.max(0, this.attackCooldown - dt);
    this.hitStun = Math.max(0, this.hitStun - dt);
    this.enrageTimer = Math.max(0, this.enrageTimer - dt);
    this.dashCd = Math.max(0, this.dashCd - dt);
    this.rangedCd = Math.max(0, this.rangedCd - dt);
    this.guardCd = Math.max(0, this.guardCd - dt);
    this.stormCd = Math.max(0, this.stormCd - dt);
    this.combatTimer += dt;
    if (this.isElite) this.smashCd = Math.max(0, this.smashCd - dt);

    // Fase 10: Storm Field (Tempest Warden) resolve independente do estado
    if (this.stormZones.length) this.updateStormField(dt, player);
    // Fase 10: fade do Cloudstalker (nunca invisível; some só parcialmente)
    if (this.behavior === "ambush") this.updateFade(dt);
    // Fase 10: inverte o sentido do strafe do Galestrider de tempos em tempos
    if (this.behavior === "strafe") {
      this.strafeFlip -= dt;
      if (this.strafeFlip <= 0) {
        this.strafeSign *= -1;
        this.strafeFlip = 2 + Math.random() * 2;
      }
    }

    // Guard (Glintback): ativa/expira
    if (this.guardActive) {
      this.guardTimer -= dt;
      if (this.guardTimer <= 0) this.setGuard(false);
    }

    // Flyer derrubado: conta o tempo no chão; volta a voar ao fim
    if (this.grounded) {
      this.groundedTimer -= dt;
      if (this.groundedTimer <= 0 && !this.pulled) this.grounded = false;
    }

    // regeneração fora de combate
    if (this.combatTimer >= this.enemyCfg.regenDelay && this.health.current < this.health.max) {
      this.health.heal(this.enemyCfg.regenRate * this.health.max * dt);
    }
    if (this.flash > 0) {
      this.flash -= dt;
      const ei = this.flash > 0 ? 1.6 : this.isElite ? 0.5 : 0.3;
      for (const m of this.rig.bodyMats) m.emissiveIntensity = ei;
      if (this.rigged) for (const m of this.rigged.bodyMats) m.emissiveIntensity = ei;
    }
    if (this.auraMat) {
      this.auraPulse += dt * 3;
      this.auraMat.opacity = 0.4 + Math.sin(this.auraPulse) * 0.18;
    }

    if (this.pulled) {
      this.combatTimer = 0;
      this.applyBodyTransforms(dt, 0);
      return;
    }

    // Elite Ground Smash em andamento (independe do resto)
    if (this.smashWindup > 0) {
      this.updateSmash(dt, player);
      this.faceTowards(player, dt);
      this.settleY();
      this.applyBodyTransforms(dt, 0);
      return;
    }

    _toPlayer.subVectors(player.position, this.group.position);
    _toPlayer.y = 0;
    const dist = _toPlayer.length();
    if (dist <= this.def.aggroRange && engageable) this.combatTimer = 0;

    this.velocity.set(0, 0, 0);

    if (this.rangedWindup > 0) {
      // carregando tiro: parado, encarando o alvo
      this.rangedWindup -= dt;
      this.velocity.set(0, 0, 0);
      if (this.rangedWindup <= 0) this.fireRanged(player);
    } else if (this.dashState !== "none") {
      this.updateDash(dt, dist, player);
    } else if (this.hitStun > 0) {
      // flinch
    } else if (this.state === "attack" && engageable) {
      this.updateAttack(dt, dist, player);
    } else {
      this.decideAndMove(dist, player, engageable);
    }

    // integra
    this.group.position.addScaledVector(this.velocity, dt * (this.slowTimer > 0 ? this.slowMult : 1)); // 10.9FC
    this.group.position.addScaledVector(this.knockback, dt);
    this.knockback.multiplyScalar(Math.exp(-10 * dt));
    this.arena.clamp(this.group.position, this.radius);
    this.settleY();

    this.faceTowards(player, dt, dist);

    // animação procedural (gait/lean/breathe)
    const moving = this.velocity.lengthSq() > 0.05 || this.dashState === "dash";
    this.applyBodyTransforms(dt, moving ? this.velocity.length() : 0);
  }

  /** Altura do grupo: flyer flutua (rig já tem y alto); grounded cai ao chão; resto = 0. */
  private settleY(): void {
    if (this.isFlyer) {
      const y = this.rig.group.position.y;
      const target = this.grounded ? -1.5 : 0;
      this.rig.group.position.y += (target - y) * 0.28;
      // queda: pequeno spin + tombamento; voando: endireita
      const falling = this.grounded && y > -1.2;
      if (falling) this.rig.group.rotation.y += 8 * (1 / 60);
      const tiltTarget = this.grounded ? 0.5 : 0;
      this.rig.group.rotation.z += (tiltTarget - this.rig.group.rotation.z) * 0.2;
      // impacto no chão (uma vez): burst de cristal + poeira
      if (this.grounded && !this.groundImpactDone && this.rig.group.position.y < -1.2) {
        this.groundImpactDone = true;
        this.fxParticles?.burst(this.group.position.x, 0.2, this.group.position.z, this.def.crystalColor ?? 0x8fe8ff, { count: 12, speed: 5, dur: 0.5 });
        this.fxParticles?.burst(this.group.position.x, 0.1, this.group.position.z, 0xbfc8d8, { count: 6, speed: 3, dur: 0.4 });
      }
      // prep de subida: leve elevação quando o tempo está acabando
      if (this.grounded && this.groundedTimer < 0.45) this.rig.group.position.y += 0.25;
    }
    this.group.position.y = 0;
  }

  private faceTowards(player: Damageable, dt: number, dist = 1): void {
    let faceTarget = this.facing;
    _toPlayer.subVectors(player.position, this.group.position);
    _toPlayer.y = 0;
    if (dist > 0.01 && (this.state === "chase" || this.state === "attack" || this.dashState !== "none")) {
      faceTarget = Math.atan2(_toPlayer.x, _toPlayer.z);
    } else if (this.velocity.lengthSq() > 0.01) {
      faceTarget = Math.atan2(this.velocity.x, this.velocity.z);
    }
    this.facing = dampAngle(this.facing, faceTarget, 10, dt);
    this.group.rotation.y = this.facing;
  }

  // ---- animação procedural ----
  private applyBodyTransforms(dt: number, speed: number): void {
    if (this.rigged) {
      // rig GLB: locomoção por clip (Move/Idle); ataque/hit/death são one-shots por evento.
      this.rigged.anim.play(speed > 0.05 ? "Move" : "Idle", { crossfade: 0.2 });
      return;
    }
    this.bob += dt * 4;
    // breathe/idle: leve pulsar do corpo
    if (this.rig.core) {
      const breathe = 1 + Math.sin(this.bob * 0.9) * 0.03;
      // 10.9AV — SQUASH ao apanhar (achata e alarga por um instante) — o golpe tem impacto visível
      const sq = this.hitStun > 0 ? 1 : 0;
      this.rig.core.scale.set(1 + sq * 0.18, breathe * (1 - sq * 0.2), 1 + sq * 0.18);
    }
    // gait: pernas alternadas ao mover
    if (this.rig.legs.length) {
      this.gaitPhase += dt * (6 + speed * 1.5);
      for (let i = 0; i < this.rig.legs.length; i++) {
        const s = Math.sin(this.gaitPhase + (i % 2) * Math.PI) * Math.min(0.4, speed * 0.12);
        this.rig.legs[i].rotation.x = s;
      }
    }
    // lean: inclina conforme o estado (carga/investida/recuperação) ou a corrida
    let targetLean = Math.min(0.18, speed * 0.05);
    if (this.dashState === "telegraph") targetLean = -0.22; // recua/agacha carregando (anticipação)
    else if (this.dashState === "dash") targetLean = 0.4; // corpo lançado à frente
    else if (this.dashState === "recover") targetLean = 0.15; // skid
    // 10.9AV — golpe normal com ANTECIPAÇÃO (recua no windup) e LUNGE (lança à frente ao acertar)
    else if (this.state === "attack" && this.attackWindup > 0) targetLean = -0.18;
    else if (this.attackCooldown > this.def.attackCooldown - 0.18) targetLean = 0.32;
    this.lean += (targetLean - this.lean) * 0.18;
    this.rig.group.rotation.x = this.lean;
    // asas do flyer: batem voando; retraídas/caídas quando derrubado
    if (this.rig.wings.length) {
      if (this.grounded) {
        for (let i = 0; i < this.rig.wings.length; i++) this.rig.wings[i].rotation.z += (-0.7 - this.rig.wings[i].rotation.z) * 0.2;
      } else {
        const flap = Math.sin(this.bob * 3) * 0.35;
        for (let i = 0; i < this.rig.wings.length; i++) this.rig.wings[i].rotation.z = flap * (i % 2 === 0 ? 1 : -1);
      }
    }
    // cauda balança
    if (this.rig.tail) this.rig.tail.rotation.y = Math.sin(this.bob) * 0.25;
  }

  // ---- decisão de movimento ----
  private decideAndMove(dist: number, player: Damageable, engageable: boolean): void {
    if (!engageable) {
      this.state = "idle";
      this.grounded = false;
      this.idleMove();
      return;
    }

    const homeDist = Math.hypot(this.group.position.x - this.home.x, this.group.position.z - this.home.z);
    if (homeDist > this.enemyCfg.maxLeash) this.returning = true;
    if (this.returning) {
      this.state = "idle";
      this.returnHome();
      if (homeDist < 2) this.returning = false;
      return;
    }

    // 10.9AB — PERSEGUIÇÃO PERSISTENTE: uma vez aggro, o inimigo SEGUE o player independente da
    // distância (antes desistia quando o player saía de ~1.7× o aggroRange e ficava kitável sem
    // apanhar). O único limite é a ÁREA DE ATUAÇÃO: se se afastar demais da origem (maxLeash), o
    // bloco `returning` acima o traz de volta pra casa — ele não abandona o território.
    if (dist <= this.def.aggroRange || this.state === "chase") {
      this.state = "chase";
      switch (this.behavior) {
        case "ranged":
          this.behaveRanged(dist, player);
          return;
        case "lunge":
        case "charge":
          this.behaveCharge(dist, player);
          return;
        case "guard":
          this.behaveGuard(dist, player);
          return;
        case "warden":
          this.behaveWarden(dist, player);
          return;
        case "strafe":
          this.behaveStrafe(dist, player);
          return;
        case "shield":
          this.behaveShield(dist, player);
          return;
        case "ambush":
          this.behaveAmbush(dist, player);
          return;
        case "tempest":
          this.behaveTempest(dist, player);
          return;
        case "hybrid":
          this.behaveHybrid(dist, player);
          return;
        default:
          this.behaveMelee(dist, player);
          return;
      }
    }
    this.state = "idle";
    this.idleMove();
  }

  /**
   * 10.9CG — IDLE: volta pra casa se a coleira estourou; senão PERAMBULA (ponto aleatório a
   * 8–18 m, anda devagar, pausa, repete). A casa acompanha o último ponto alcançado, então o
   * monstro vai cruzando o mapa da região com o tempo. Emboscadores (Cloudstalker) ficam parados.
   */
  private idleMove(): void {
    if (this.behavior === "ambush") { this.returnHome(); return; }
    const w = GameConfig.enemyCombat.wander;
    const dt = this.dtNow;
    if (this.wanderWait > 0) { this.wanderWait -= dt; return; } // parado, olhando em volta
    if (!this.wandering) {
      const a = Math.random() * Math.PI * 2;
      const r = w.minDist + Math.random() * (w.maxDist - w.minDist);
      this.wanderTarget.set(this.group.position.x + Math.sin(a) * r, 0, this.group.position.z + Math.cos(a) * r);
      this.arena.clamp(this.wanderTarget, this.radius + 1);
      this.wandering = true;
    }
    _toHome.subVectors(this.wanderTarget, this.group.position);
    _toHome.y = 0;
    const d = _toHome.length();
    if (d < 1.2) {
      this.wandering = false;
      this.wanderWait = w.pauseMin + Math.random() * (w.pauseMax - w.pauseMin);
      this.home.copy(this.group.position); // a coleira passa a valer daqui
      return;
    }
    this.velocity.copy(_toHome).multiplyScalar((this.def.moveSpeed * w.speedMult) / d);
  }

  private approach(dist: number, mult = 1): void {
    if (dist > this.def.attackRange * 0.6) {
      const rage = this.enrageTimer > 0 ? 1.4 : 1;
      this.velocity.copy(_toPlayer).multiplyScalar((this.def.moveSpeed * mult * rage) / Math.max(dist, 0.001));
    }
  }

  private behaveMelee(dist: number, player: Damageable): void {
    if (this.isElite && this.smashCd <= 0 && !player.isDead && dist <= this.def.attackRange + (this.def.elite!.groundSmash.radius) + 2) {
      this.startSmash(player);
      return;
    }
    if (dist <= this.def.attackRange && this.attackCooldown <= 0 && !player.isDead) {
      this.startAttack();
      return;
    }
    this.approach(dist);
  }

  private behaveGuard(dist: number, player: Damageable): void {
    // ativa a carapaça periodicamente (em combate)
    if (!this.guardActive && this.guardCd <= 0) this.setGuard(true);
    this.behaveMelee(dist, player);
  }

  private behaveCharge(dist: number, player: Damageable): void {
    const cfg = this.dashCfg();
    if (cfg && this.dashCd <= 0 && !player.isDead && dist <= cfg.maxDist && dist > this.def.attackRange * 0.8) {
      this.startDash(player);
      return;
    }
    // fora do alcance de investida: aproxima / ou ataca de perto
    if (dist <= this.def.attackRange && this.attackCooldown <= 0 && !player.isDead) {
      this.startAttack();
      return;
    }
    this.approach(dist, this.behavior === "lunge" ? 1.1 : 0.9);
  }

  private behaveRanged(dist: number, player: Damageable): void {
    const kite = this.def.attackRange * 0.55;
    if (dist < kite) {
      // recua mantendo distância
      this.velocity.copy(_toPlayer).multiplyScalar(-this.def.moveSpeed / Math.max(dist, 0.001));
    } else if (dist > this.def.attackRange) {
      this.approach(dist);
    }
    if (this.rangedCd <= 0 && this.rangedWindup <= 0 && dist <= this.def.attackRange && !player.isDead) {
      this.startRanged();
    }
  }

  /**
   * 10.6F — PERTO E LONGE. O bicho corpo-a-corpo virava um alvo grátis: bastava ficar a 5 m
   * batendo de arco. Agora, fora do alcance de melee, ele ARREMESSA (com o mesmo telegraph
   * de windup dos atiradores); dentro do alcance, bate normal. Não faz kite: continua vindo
   * pra cima, então dá pra encostar nele — só não dá mais pra ignorar a distância.
   */
  private behaveHybrid(dist: number, player: Damageable): void {
    if (dist <= this.def.attackRange && this.attackCooldown <= 0 && !player.isDead) {
      this.startAttack();
      return;
    }
    const r = this.def.ranged;
    if (r && dist > this.def.attackRange && dist <= (r.range ?? 13) && this.rangedCd <= 0 && this.rangedWindup <= 0 && !player.isDead) {
      this.startRanged();
      return;
    }
    this.approach(dist);
  }

  private behaveWarden(dist: number, player: Damageable): void {
    // rotação: 0 = charge, 1 = shard burst, 2 = ground smash, senão aproxima/melee
    if (this.smashCd <= 0 && this.wardenAttack === 2 && dist <= 8) {
      this.startSmash(player);
      this.wardenAttack = 0;
      return;
    }
    if (this.def.charge && this.dashCd <= 0 && this.wardenAttack === 0 && dist <= this.def.charge.maxDist) {
      this.startDash(player);
      this.wardenAttack = 1;
      return;
    }
    if (this.def.ranged && this.rangedCd <= 0 && this.rangedWindup <= 0 && this.wardenAttack === 1 && dist <= 14) {
      this.startRanged();
      this.wardenAttack = 2;
      return;
    }
    if (dist <= this.def.attackRange && this.attackCooldown <= 0 && !player.isDead) {
      this.startAttack();
      return;
    }
    this.approach(dist);
  }

  // ---- Fase 10: Galestrider (strafe) ----
  private behaveStrafe(dist: number, player: Damageable): void {
    const cfg = this.def.charge;
    // dash lateral/investida quando pronto e em média distância
    if (cfg && this.dashCd <= 0 && dist <= cfg.maxDist && dist > this.def.attackRange && !player.isDead) {
      this.startDash(player);
      return;
    }
    if (dist <= this.def.attackRange && this.attackCooldown <= 0 && !player.isDead) {
      this.startAttack();
      return;
    }
    // circula: componente perpendicular (strafe) + mantém raio de órbita
    const orbit = this.def.aggroRange * 0.4;
    const radial = _toPlayer.clone();
    radial.y = 0;
    if (radial.lengthSq() < 1e-4) radial.set(0, 0, 1);
    radial.normalize();
    const spd = this.def.moveSpeed;
    const perpX = -radial.z * this.strafeSign;
    const perpZ = radial.x * this.strafeSign;
    let vx = perpX * spd * 0.85;
    let vz = perpZ * spd * 0.85;
    if (dist > orbit + 1.5) { vx += radial.x * spd * 0.6; vz += radial.z * spd * 0.6; }
    else if (dist < orbit - 1.5) { vx -= radial.x * spd * 0.6; vz -= radial.z * spd * 0.6; }
    this.velocity.set(vx, 0, vz);
  }

  // ---- Fase 10: Stormback (shield) ----
  private behaveShield(dist: number, player: Damageable): void {
    if (!this.guardActive && this.guardCd <= 0) this.setGuard(true);
    this.behaveMelee(dist, player);
  }

  // ---- Fase 10: Cloudstalker (ambush) = charge (surge) + fade parcial ----
  private behaveAmbush(dist: number, player: Damageable): void {
    // usa a máquina de dash (via dashCfg sintetizado do ambush); o fade é tratado no update
    this.behaveCharge(dist, player);
  }

  /** Config de dash: charge nativo OU sintetizado do ambush (Cloudstalker). */
  private dashCfg(): NonNullable<EnemyDef["charge"]> | null {
    if (this.def.charge) return this.def.charge;
    const a = this.def.ambush;
    if (a) return { telegraph: a.telegraph, speed: a.surgeSpeed, damage: a.surgeDamage, maxDist: a.stalkRange + 3, cooldown: a.cooldown, knockback: a.knockback, recovery: 0.5 };
    return null;
  }

  /** Fade parcial do Cloudstalker: some ao perseguir, materializa ao telegraph/surge/golpe. */
  private updateFade(dt: number): void {
    const a = this.def.ambush;
    if (!a) return;
    const materialized = this.dashState !== "none" || this.hitStun > 0 || this.state === "attack";
    const target = materialized ? 1 : a.fadeOpacity;
    this.fadeOpacity += (target - this.fadeOpacity) * Math.min(1, dt * 6);
    for (const m of this.rig.bodyMats) m.opacity = this.fadeOpacity;
    for (const m of this.rig.crystalMats) m.opacity = Math.max(this.fadeOpacity, 0.5);
  }

  // ---- Fase 10: Tempest Warden (tempest) — Arc Dash + Storm Field + Ground Smash ----
  private behaveTempest(dist: number, player: Damageable): void {
    if (this.def.charge && this.dashCd <= 0 && this.tempestAttack === 0 && dist <= this.def.charge.maxDist && !player.isDead) {
      this.startDash(player); // Arc Dash (deixa a linha telegrafada)
      this.tempestAttack = 1;
      return;
    }
    if (this.def.stormField && this.stormCd <= 0 && this.tempestAttack === 1 && dist <= 15 && !player.isDead) {
      this.startStormField(player);
      this.tempestAttack = 2;
      return;
    }
    if (this.isElite && this.smashCd <= 0 && this.tempestAttack === 2 && dist <= 8 && !player.isDead) {
      this.startSmash(player);
      this.tempestAttack = 0;
      return;
    }
    if (dist <= this.def.attackRange && this.attackCooldown <= 0 && !player.isDead) {
      this.startAttack();
      return;
    }
    this.approach(dist);
  }

  private startStormField(player: Damageable): void {
    const cfg = this.def.stormField!;
    this.stormCd = cfg.cooldown;
    const color = this.def.elite?.aura ?? this.def.crystalColor ?? 0x6fb8ff;
    for (let i = 0; i < cfg.zones; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = Math.random() * 4.5;
      const x = player.position.x + Math.cos(a) * r;
      const z = player.position.z + Math.sin(a) * r;
      this.fxTelegraph?.circle(x, z, cfg.radius, cfg.telegraph, color);
      this.stormZones.push({ x, z, t: cfg.telegraph });
    }
    for (const m of this.rig.crystalMats) m.emissiveIntensity = 2.8;
  }

  private updateStormField(dt: number, player: Damageable): void {
    const cfg = this.def.stormField!;
    for (let i = this.stormZones.length - 1; i >= 0; i--) {
      const z = this.stormZones[i];
      z.t -= dt;
      if (z.t > 0) continue;
      // energiza: dano se o player estiver dentro no impacto
      const dx = player.position.x - z.x;
      const dz = player.position.z - z.z;
      if (dx * dx + dz * dz <= cfg.radius * cfg.radius && !player.isDead && !player.invulnerable) {
        _stormFrom.set(z.x, 0, z.z);
        dealDamage(player, { baseDamage: cfg.damage * this.surgeDmg * this.dmgScale, critChance: 0, critDamageMult: 1, armor: 0 }, { targetKind: "player", attackerRank: this.attackerRank, damageType: "piercing", knockbackFrom: _stormFrom, knockbackForce: 6 });
      }
      this.fxParticles?.burst(z.x, 0.3, z.z, this.def.elite?.aura ?? 0x6fb8ff, { count: 14, speed: 6, dur: 0.55 });
      this.stormZones.splice(i, 1);
    }
  }

  private setGuard(on: boolean): void {
    this.guardActive = on;
    if (on) {
      this.guardTimer = this.def.guard?.duration ?? 3;
    } else {
      this.guardCd = this.def.guard?.cooldown ?? 6;
    }
    // feedback visual: cristais acendem quando protegido
    for (const m of this.rig.crystalMats) m.emissiveIntensity = on ? 2.4 : 1.2;
    // Stormback: bolha de Static Shield visível durante o escudo
    if (this.rig.shield) this.rig.shield.visible = on;
  }

  // ---- Dash (lunge/charge/strafe/arc dash/ambush surge) ----
  private startDash(player: Damageable): void {
    const cfg = this.dashCfg()!;
    this.dashState = "telegraph";
    this.dashTimer = cfg.telegraph;
    this.dashDir.subVectors(player.position, this.group.position);
    this.dashDir.y = 0;
    if (this.dashDir.lengthSq() < 1e-4) this.dashDir.set(0, 0, 1);
    this.dashDir.normalize();
    this.dashTravel = 0;
    this.dashHitDone = false;
    // telegraph: CORREDOR de ameaça (lane) na direção da investida — leitura WHERE/WHEN/HOW BIG.
    // Elite (warden) = corredor mais largo (mais intenso).
    if (this.fxTelegraph) {
      const angle = Math.atan2(this.dashDir.x, this.dashDir.z);
      const width = this.radius * 2 + (this.isElite ? 1.6 : 0.8);
      this.dashTeleId = this.fxTelegraph.lane(this.group.position.x, this.group.position.z, angle, cfg.maxDist, width, cfg.telegraph, this.def.crystalColor ?? 0x8ea2ff);
    }
    // energia carregando nos cristais/chifres + poeira ao "raspar" o chão
    for (const m of this.rig.crystalMats) m.emissiveIntensity = this.isElite ? 3.0 : 2.4;
    this.fxParticles?.burst(this.group.position.x, 0.2, this.group.position.z, this.def.crystalColor ?? 0x8ea2ff, { count: this.isElite ? 8 : 5, speed: 2.5, dur: 0.4 });
    this.state = "chase";
  }

  private updateDash(dt: number, dist: number, player: Damageable): void {
    const cfg = this.dashCfg()!;
    if (this.dashState === "telegraph") {
      this.dashTimer -= dt;
      this.velocity.set(0, 0, 0);
      if (this.dashTimer <= 0) {
        this.dashState = "dash";
        this.dashTimer = 1.2; // teto de duração
        this.dashTeleId = 0; // o lane some sozinho ao fim da duração
      }
      return;
    }
    if (this.dashState === "dash") {
      const step = cfg.speed * dt;
      this.velocity.copy(this.dashDir).multiplyScalar(cfg.speed);
      this.dashTravel += step;
      this.dashTimer -= dt;
      // rastro de poeira/energia durante a investida (leve)
      if (Math.random() < 0.5) this.fxParticles?.burst(this.group.position.x, 0.15, this.group.position.z, this.def.crystalColor ?? 0x8ea2ff, { count: this.isElite ? 4 : 2, speed: 2, dur: 0.35 });
      // dano por contato (uma vez)
      if (!this.dashHitDone && dist <= this.radius + 1.0 && !player.isDead && !player.invulnerable) {
        dealDamage(player, { baseDamage: cfg.damage * this.surgeDmg * this.dmgScale, critChance: 0, critDamageMult: 1, armor: 0 }, { targetKind: "player", attackerRank: this.attackerRank, damageType: this.dmgType, knockbackFrom: this.group.position, knockbackForce: cfg.knockback });
        this.dashHitDone = true;
      }
      if (this.dashTravel >= cfg.maxDist || this.dashTimer <= 0 || this.dashHitDone) {
        this.dashState = "recover";
        this.dashTimer = cfg.recovery;
        this.velocity.set(0, 0, 0);
        this.dashCd = cfg.cooldown;
        for (const m of this.rig.crystalMats) m.emissiveIntensity = 1.2;
        // skid: poeira de derrapagem (janela de punição)
        this.fxParticles?.burst(this.group.position.x, 0.2, this.group.position.z, 0xbfc8d8, { count: 6, speed: 4, dur: 0.4 });
      }
      return;
    }
    // recover (skid): corpo ainda inclinado (lean via applyBodyTransforms)
    this.dashTimer -= dt;
    this.velocity.set(0, 0, 0);
    if (this.dashTimer <= 0) this.dashState = "none";
  }

  // ---- Ranged (aether bolt / shard burst / arc bolt) ----
  private startRanged(): void {
    this.rangedWindup = this.def.ranged?.windup ?? 0.5;
    this.burstLeft = Math.max(0, (this.def.ranged?.burst ?? 1) - 1); // tiros extras após o 1º
    this.velocity.set(0, 0, 0);
    for (const m of this.rig.crystalMats) m.emissiveIntensity = 2.6;
  }
  private fireRanged(player: Damageable): void {
    this.rangedWindup = 0;
    const cfg = this.def.ranged!;
    const dmg = (cfg.damage ?? this.def.damage) * this.surgeDmg;
    const origin = this.group.position.clone();
    origin.y = this.isFlyer ? 1.8 : 1.2;
    const dir = new THREE.Vector3().subVectors(player.position, origin);
    dir.y = 0;
    // 10.9AI — MIRA ANTECIPADA: aponta pra onde o player VAI estar quando o tiro chegar
    // (distância / velocidade do projétil). Antes mirava a posição ATUAL: a ~1 s de voo o player
    // já tinha andado 3-6 m e os atiradores nunca acertavam quem se movia. Fator 0.9 = leve
    // sub-antecipação (ainda dá pra jukar mudando de direção — morte por erro legível).
    const vel = player.moveVelocity;
    if (vel) {
      const travel = Math.min(1.6, dir.length() / Math.max(1, cfg.projectileSpeed));
      dir.x += vel.x * travel * 0.9;
      dir.z += vel.z * travel * 0.9;
    }
    dir.normalize();
    if (this.behavior === "warden") {
      // Shard Burst: leque de 3
      for (const off of [-0.25, 0, 0.25]) {
        const c = Math.cos(off), s = Math.sin(off);
        this.fireBolt?.(origin, dir.x * c - dir.z * s, dir.x * s + dir.z * c, cfg.projectileSpeed, dmg, cfg.projectileColor);
      }
    } else {
      this.fireBolt?.(origin, dir.x, dir.z, cfg.projectileSpeed, dmg, cfg.projectileColor);
    }
    // Arc Bolt (Arcbeak): dispara os tiros restantes do burst com um pequeno intervalo
    if (this.burstLeft > 0) {
      this.burstLeft--;
      this.rangedWindup = cfg.burstGap ?? 0.16; // próximo tiro do burst (sem cooldown cheio)
      for (const m of this.rig.crystalMats) m.emissiveIntensity = 2.6;
    } else {
      for (const m of this.rig.crystalMats) m.emissiveIntensity = this.guardActive ? 2.4 : 1.2;
      this.rangedCd = cfg.cooldown;
    }
  }

  private returnHome(): void {
    _toHome.subVectors(this.home, this.group.position);
    _toHome.y = 0;
    const d = _toHome.length();
    if (d > 0.5) this.velocity.copy(_toHome).multiplyScalar((this.def.moveSpeed * 0.6) / d);
  }

  private startAttack(): void {
    this.state = "attack";
    this.attackWindup = this.def.attackWindup;
    this.rigged?.anim.playOnce("Attack", "Idle"); // anticipation vem do windup do clip (§7)
  }

  private updateAttack(dt: number, dist: number, player: Damageable): void {
    this.attackWindup -= dt;
    if (this.attackWindup <= 0) {
      if (dist <= this.def.attackRange + 0.5 && !player.isDead && !player.invulnerable
        && !wallBetween(this.group.position.x, this.group.position.z, player.position.x, player.position.z)) {
        dealDamage(player, this.combatStats, { targetKind: "player", attackerRank: this.attackerRank, damageType: this.dmgType, knockbackFrom: this.group.position, knockbackForce: 3 });
      }
      this.attackCooldown = this.def.attackCooldown;
      this.state = "chase";
    }
  }

  // ---- Elite Ground Smash ----
  private startSmash(player: Damageable): void {
    const gs = this.def.elite!.groundSmash;
    this.smashWindup = gs.telegraph;
    this.smashCenter.copy(player.position);
    this.smashCenter.y = 0;
    this.velocity.set(0, 0, 0);
    this.state = "chase";
    this.smashTeleId = this.fxTelegraph?.circle(this.smashCenter.x, this.smashCenter.z, gs.radius, gs.telegraph, this.def.elite!.aura) ?? 0;
  }
  private updateSmash(dt: number, player: Damageable): void {
    this.smashWindup -= dt;
    if (this.smashWindup > 0) return;
    this.smashWindup = 0;
    const gs = this.def.elite!.groundSmash;
    const dx = player.position.x - this.smashCenter.x;
    const dz = player.position.z - this.smashCenter.z;
    if (dx * dx + dz * dz <= gs.radius * gs.radius && !player.isDead && !player.invulnerable) {
      dealDamage(player, { baseDamage: gs.damage * this.surgeDmg * this.dmgScale, critChance: 0, critDamageMult: 1, armor: 0 }, { targetKind: "player", attackerRank: this.attackerRank, damageType: "blunt", knockbackFrom: this.smashCenter, knockbackForce: gs.knockback });
    }
    this.fxParticles?.burst(this.smashCenter.x, 0.3, this.smashCenter.z, this.def.elite!.aura, { count: 16, speed: 6, dur: 0.6 });
    this.smashCd = gs.cooldown;
  }

  private die(): void {
    if (this.state === "dead") return;
    this.state = "dead";
    this.velocity.set(0, 0, 0);
    this.deathTimer = 0.62; // 10.9GS: um pouco mais longo — dá tempo de ver a dissolução
    this.rigged?.anim.play("Death", { loop: false }); // §9: hit → death → dissolve (abaixo)
    // remove telegraphs pendentes (morte não deixa "fantasma" de charge/smash)
    if (this.dashTeleId) this.fxTelegraph?.cancel(this.dashTeleId);
    if (this.smashTeleId) this.fxTelegraph?.cancel(this.smashTeleId);
    this.dashTeleId = 0;
    this.smashTeleId = 0;
    // 10.7D/H — XP escala com o Threat + tier, mas com peso MENOR (0.6→0.3): monstro forte
    // dá mais XP, sem virar farm explosivo que zera o jogo em dias. Progressão de semanas.
    const scale = Math.max(1, this.health.max / Math.max(1, this.def.maxHp));
    const xp = Math.max(1, Math.round(this.def.xpReward * Math.min(GameConfig.progression.xpKillScaleCap, 1 + (scale - 1) * 0.3))); // 10.9BM: teto
    events.emit("enemy:killed", { enemyId: this.def.id, xpReward: xp, position: this.group.position.clone(), drops: this.def.drops, elite: this.isElite, eventMonster: this.eventMonster });
    if (this.def.elite) events.emit("elite:killed", { position: this.group.position.clone() });
  }

  private updateDeath(dt: number): void {
    this.deathTimer -= dt;
    const t = Math.max(0, this.deathTimer / 0.62);
    // 10.9GS — dissolve em cubos de energia (shader) em vez de só encolher; encolhe pouco no fim
    const ds = 1 - t;
    for (const u of this.dissolve) u.value = ds * ds * 1.05;
    this.group.scale.setScalar(this.def.scale * (0.7 + 0.3 * t));
    for (const m of this.rig.bodyMats) m.emissiveIntensity = 1.2 + 1.6 * ds;
    this.group.position.y += dt * 0.9;
    this.rig.group.rotation.z += dt * 2.2; // tomba ao dissolver
    if (this.deathTimer <= 0) this.removeRequested = true;
  }

  dispose(): void {
    const materials = new Set<THREE.Material>();
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        if (!o.geometry.userData.shared) o.geometry.dispose();
        for (const m of Array.isArray(o.material) ? o.material : [o.material]) {
          if (!m.userData.shared) materials.add(m);
        }
      }
    });
    for (const m of materials) m.dispose();
    this.rigged?.dispose();
  }
}
